import React, { useState } from 'react';
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Loader from './../components/Loader';
import Message from './../components/Message';

import {
  useGetIncomesQuery,
  useDeleteIncomeMutation,
  useCreateIncomeMutation,
} from './../slices/incomesApiSlice';
import { toast } from 'react-toastify';

const CashUserList = () => {
  
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, error, refetch } = useGetIncomesQuery({ pageNumber });
  const [deleteIncome, { isLoading: loadingDelete }] = useDeleteIncomeMutation();
  const [createIncome, { isLoading: loadingCreate }] = useCreateIncomeMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Create a new income
  const createIncomeHandler = async () => {
    if (window.confirm('Are you sure you want to create a new income?')) {
      try {
        const newIncome = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New Income',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createIncome(newIncome).unwrap();
        toast.success('New income created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an income
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this income?')) {
      try {
        await deleteIncome(id).unwrap();
        toast.success('Income deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort incomes
  const filterAndSortIncomes = () => {
    let filteredIncomes = [...(data?.incomes || [])];

    // Filter by text input
    if (filter) {
      filteredIncomes = filteredIncomes.filter(
        (income) =>
          income.account?.toLowerCase().includes(filter.toLowerCase()) ||
          income.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          income.details?.toLowerCase().includes(filter.toLowerCase()) ||
          income.category?.toLowerCase().includes(filter.toLowerCase()) ||
          income.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          income.notes?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredIncomes = filteredIncomes.filter((income) => {
        const incomeDate = new Date(income.date);
        return incomeDate >= start && incomeDate <= end;
      });
    }

    // Sort incomes
    if (sortField) {
      filteredIncomes.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredIncomes;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredIncomes = data?.incomes?.filter((income) => {
      const incomeDate = new Date(income.date);
      return incomeDate >= start && incomeDate <= end;
    }) || [];

    return filteredIncomes.reduce((acc, income) => acc + income.amount, 0);
  };

  // Calculate total for all incomes
  const totalAmount = data?.incomes?.reduce((acc, income) => acc + income.amount, 0) || 0;

   // Handle pagination changes
   const paginationHandler = (page) => {
    setPageNumber(page);
  };

  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Cash Collected</h1>
        </Col>
        <Col className="text-end">
          <Button onClick={createIncomeHandler} className="my-3" style={{display:"none"}}>
            <FaPlus /> Add New Income
          </Button>
          <Link to='/create/staff/income' className='btn btn-light my-3'>
           Add Data
          </Link>
          <Link to='/create/income/excelform' className='btn btn-light my-3'>
           AddFromExcel
          </Link>
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: D{totalAmount.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): D{calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.incomes?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                COLLECTOR {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortIncomes().map((income) => (
                <tr key={income._id}>
                  <td>{new Date(income.date).toLocaleDateString()}</td>
                
                  <td>{income.accountName}</td>
                
                  <td>
                    {income.amount >= 0
                      ? `${income.amount.toFixed(2)}`
                      : `-${Math.abs(income.amount).toFixed(2)}`}
                  </td>
                  <td>{income.category}</td>
                  
                  <td className="thumbnails">
                    {income.images?.length > 0 && (
                      <img
                        src={income.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    <Link
                     className="btn btn-primary btn-sm m-2"
                    to={`/staffincome/${income._id}`}
                    role="button"
                    >
                       Details
                    </Link>
                    <Button
                      style={{display:"none"}}
                      as={Link}
                      to={`/income/${income._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                     style={{display: "none"}}
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(income._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
           <Pagination>
                                  {[...Array(data.pages).keys()].map((x) => (
                                    <Pagination.Item
                                      key={x + 1}
                                      active={x + 1 === pageNumber}
                                      onClick={() => paginationHandler(x + 1)}
                                    >
                                      {x + 1}
                                    </Pagination.Item>
                                  ))}
             </Pagination>
           
          
        </>
      ) : (
        <Message variant="info">No incomes found</Message>
      )}
    </>
  );
};

export default CashUserList;